import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Alert, 
  CircularProgress, 
  Snackbar,
  Paper,
  Container,
  Stack,
  Button,
} from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import CalendarEditable from "../../components/CalendarEditable/CalendarEditable";
import CourtEventApi, {
  CourtEventDto,
  UpdateCourtEventDto,
  isAwaitingAppearanceEvent,
} from "../../api/CourtEventApi";
import { DateTime } from 'luxon';
import { View } from 'react-big-calendar';
import { staffTicketUrl } from '../../utils/appearanceAlertUrls';
import './ManagerCalendarView.scss';

const awaitingFetchWindow = () => {
  const now = DateTime.now();
  return {
    start: now.minus({ months: 6 }).startOf('day'),
    end: now.plus({ months: 1 }).endOf('day'),
  };
};

const ManagerCalendarView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const awaitingFilter = searchParams.get('appearanceOutcome') === 'awaiting';
  const [courtEvents, setCourtEvents] = useState<CourtEventDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (awaitingFilter) {
      const { start, end } = awaitingFetchWindow();
      loadCourtEvents(start, end);
    } else {
      loadCourtEvents();
    }
  }, [awaitingFilter]);

  const loadCourtEvents = async (fromDate?: DateTime, toDate?: DateTime) => {
    try {
      // setLoading(true);
      setError(null);

      // Get court events for the specified date range or default to this month
      const now = DateTime.now();
      const startDate = fromDate || now.startOf('month');
      const endDate = toDate || now.endOf('month');

      const events = await CourtEventApi.listCourtEvents({
        CourtEvent_Start_From: startDate.toMillis(),
        CourtEvent_Start_To: endDate.toMillis()
      });

      setCourtEvents(events);
    } catch (err) {
      console.log(err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load court events';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvent = async (event: CourtEventDto): Promise<boolean> => {
    try {
      const updateDto: UpdateCourtEventDto = {
        CourtEvent_guid: event.CourtEvent_guid,
        CourtEvent_Start: event.CourtEvent_Start,
        CourtEvent_End: event.CourtEvent_End,
        CourtEvent_Title: event.CourtEvent_Title,
        CourtEvent_Desc: event.CourtEvent_Desc,
        Ticket_guid: event.Ticket_guid
      };

      const updatedEvent = await CourtEventApi.updateCourtEvent(updateDto);
      
      // Update the local state with the updated event
      setCourtEvents(prevEvents => 
        prevEvents.map(e => 
          e.CourtEvent_guid === updatedEvent.CourtEvent_guid ? updatedEvent : e
        )
      );

      showSnackbar('Court event updated successfully', 'success');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update court event';
      showSnackbar(errorMessage, 'error');
      return false;
    }
  };

  const handleDeleteEvent = async (event: CourtEventDto): Promise<boolean> => {
    try {
      await CourtEventApi.deleteCourtEvent(event.CourtEvent_guid);
      
      // Remove the deleted event from local state
      setCourtEvents(prevEvents => 
        prevEvents.filter(e => e.CourtEvent_guid !== event.CourtEvent_guid)
      );

      showSnackbar('Court event deleted successfully', 'success');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete court event';
      showSnackbar(errorMessage, 'error');
      return false;
    }
  };

  const handleRangeChange = (date: Date, view: View) => {
    if (awaitingFilter) {
      // Keep the wide window so changing month does not drop awaiting rows from other months.
      const { start, end } = awaitingFetchWindow();
      loadCourtEvents(start, end);
      return;
    }

    const luxonDate = DateTime.fromJSDate(date);
    let startDate: DateTime;
    let endDate: DateTime;

    // Calculate date range based on the calendar view
    switch (view) {
      case 'month':
        // Load entire month plus a few days buffer
        startDate = luxonDate.startOf('month').minus({ days: 7 });
        endDate = luxonDate.endOf('month').plus({ days: 7 });
        break;
      case 'week':
        // Load the week plus buffer
        startDate = luxonDate.startOf('week').minus({ days: 3 });
        endDate = luxonDate.endOf('week').plus({ days: 3 });
        break;
      case 'day':
        // Load the day plus buffer
        startDate = luxonDate.minus({ days: 1 });
        endDate = luxonDate.plus({ days: 1 });
        break;
      case 'agenda':
        // Load a month for agenda view
        startDate = luxonDate.minus({ days: 15 });
        endDate = luxonDate.plus({ days: 45 });
        break;
      default:
        // Default to month view
        startDate = luxonDate.startOf('month').minus({ days: 7 });
        endDate = luxonDate.endOf('month').plus({ days: 7 });
    }

    // Load events for the new date range
    loadCourtEvents(startDate, endDate);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const visibleEvents = awaitingFilter
    ? courtEvents.filter((event) => isAwaitingAppearanceEvent(event))
    : courtEvents;

  if (loading) {
    return (
      <>
        <Container maxWidth="xl" className="manager-calendar-view">
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  if (error && courtEvents.length === 0) {
    return (
      <>
        <Container maxWidth="xl" className="manager-calendar-view">
          <Box sx={{ mt: 3 }}>
            <Alert severity="error">
              {error}
            </Alert>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <Container maxWidth="xl" className="manager-calendar-view">
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Court Events Calendar
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {awaitingFilter
            ? 'Settings that required the client to attend, have passed, and still have no appeared / did not appear answer.'
            : 'Manage court events for the next 30 days. Click on events to view details, make changes, or handle scheduling conflicts.'}
        </Typography>
      </Box>

      {awaitingFilter && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing only unanswered required appearances from the last six months.
          Other hearings are hidden until this filter is cleared.
        </Alert>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some events may not have loaded properly: {error}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 2 }}>
        <CalendarEditable
          courtEvents={visibleEvents}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          onRangeChange={handleRangeChange}
        />
      </Paper>

      {awaitingFilter && (
        <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Unanswered required appearances ({visibleEvents.length})
          </Typography>
          {visibleEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No required appearances in the last six months are still unanswered.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {visibleEvents.map((event) => (
                <Box
                  key={event.CourtEvent_guid}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}
                >
                  <Typography variant="body2">
                    {DateTime.fromMillis(Number(event.CourtEvent_Start)).toFormat('yyyy-LL-dd')}
                    {' — '}
                    {event.CourtEvent_Title || 'Court event'}
                  </Typography>
                  {event.Ticket_guid && (
                    <Button
                      component={Link}
                      to={staffTicketUrl(event.Ticket_guid, event.CourtEvent_guid)}
                      size="small"
                    >
                      Open case
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default ManagerCalendarView;
